import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { ScheduleOrg, ScheduleProp } from '@wetags/schedule';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';

const ENABLED_CF_STATES = [State.ACTIVE, State.PROMOTE];

const useLocationCustomizationFlags = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { getCustomizationFlag } = useAppFlagStore();
  const { isFlagStoreLoaded } = useScopedAppFlagStore();

  return useMemo(() => {
    const hasEnabledFlag = (featureEnum: Feature, locationId: string) => {
      const [flag] = getCustomizationFlag({ locationIds: [locationId], featureEnum });
      return !!flag?.state && ENABLED_CF_STATES.includes(flag.state);
    };

    const weaveAirCfEnabledLocationIds = selectedLocationIds.filter((locationId) =>
      [Feature.WEAVE_MESSAGING_AIR, Feature.WEAVE_VOICE_AIR].some((featureEnum) =>
        hasEnabledFlag(featureEnum, locationId)
      )
    );

    const onlineSchedulingEnabledLocationIds = selectedLocationIds.filter((locationId) =>
      hasEnabledFlag(Feature.ONLINE_SCHEDULING, locationId)
    );

    return {
      weaveAirCfEnabledLocationIds,
      onlineSchedulingEnabledLocationIds,
      isLoading: !isFlagStoreLoaded,
    };
  }, [getCustomizationFlag, isFlagStoreLoaded, selectedLocationIds]);
};

const usePreferenceRequestAirAccess = () => {
  const { weaveAirCfEnabledLocationIds, isLoading } = useLocationCustomizationFlags();

  return {
    airEnabledLocationIds: weaveAirCfEnabledLocationIds,
    isLoadingAirEnabledLocationIds: isLoading,
  };
};

/**
 * NOTE: There is currently no hook to get the enabled location IDs for the `partner-schedule-requests` wetag.
 * The only available hook is `useWetagQueries`, which is deprecated.
 *
 * For now, all globally selected locations will be returned as having access to the partner schedule requests feature.
 * This is temporary solution until we start using customization flags to manage access.
 *
 */
const usePartnerRequestAccess = () => {
  const { selectedLocationIds } = useAppScopeStore();

  const [isPartnerRequestOrgWetagEnabled, isLoadingPartnerRequestOrgWetagEnabled] =
    ScheduleOrg.useWetag('partner-schedule-requests');

  const [isPartnerRequestLocationsWetagEnabled, isLoadingPartnerRequestLocationsWetagEnabled] =
    ScheduleProp.useWetagBulk('partner-schedule-requests', selectedLocationIds);

  return {
    partnerRequestLocationIds:
      isPartnerRequestOrgWetagEnabled || isPartnerRequestLocationsWetagEnabled ? selectedLocationIds : [],
    isLoadingPartnerRequestLocationIds:
      isLoadingPartnerRequestOrgWetagEnabled || isLoadingPartnerRequestLocationsWetagEnabled,
  };
};

// custom hook to check access for schedule request features
export const useScheduleRequestFeaturesAccess = () => {
  const { airEnabledLocationIds, isLoadingAirEnabledLocationIds } = usePreferenceRequestAirAccess();
  const { partnerRequestLocationIds, isLoadingPartnerRequestLocationIds } = usePartnerRequestAccess();
  const { onlineSchedulingEnabledLocationIds, isLoading: isLoadingCustomizationFlags } =
    useLocationCustomizationFlags();

  const enabledLocationIds = Array.from(
    new Set([...onlineSchedulingEnabledLocationIds, ...airEnabledLocationIds, ...partnerRequestLocationIds])
  );

  const hasAccess = enabledLocationIds.length > 0;
  const isLoading = isLoadingAirEnabledLocationIds || isLoadingCustomizationFlags || isLoadingPartnerRequestLocationIds;

  return {
    isLoading,
    hasScheduleRequestFeaturesAccess: !isLoading && hasAccess,
    scheduleRequestFeatureEnabledLocationIds: enabledLocationIds,
    hasAirScheduleRequestAccess: airEnabledLocationIds.length > 0,
  };
};
